// src/services/roleManagementService.js

import api from '../fapis/client';
import endpoints from '../fapis/endpoints';

const formatErrorMessage = (error) => {
  if (error.message === 'Failed to fetch' || error.code === 'ERR_NETWORK') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again.';
  }
  if (error.response) {
    const status = error.response.status;
    if (status === 403) {
      return error.response.data?.detail || 'You do not have permission to perform this action.';
    }
    if (status === 401) {
      return 'Your session has expired. Please login again.';
    }
    if (status === 404) {
      return error.response.data?.detail || 'Role not found.';
    }
    return error.response.data?.detail || error.response.data?.message || error.message;
  }
  return error.message || 'An unexpected error occurred. Please try again.';
};

export const roleManagementService = {
  // Get all roles
  getRoles: async () => {
    try {
      const response = await api.get(endpoints.retailone.roles);
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get roles error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: []
      };
    }
  },

  // Get single role
  getRole: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'Role ID is required',
        status: 400
      };
    }
    try {
      const response = await api.get(endpoints.retailone.rolesGet(uuid));
      return {
        success: true,
        data: response.data || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Get role ${uuid} error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  },

  // Create role
  createRole: async (name, description = '') => {
    if (!name) {
      return {
        success: false,
        message: 'Role name is required',
        status: 400
      };
    }
    try {
      const response = await api.post(endpoints.retailone.rolesCreate, {
        name,
        description
      });
      return {
        success: response.data.success || false,
        message: response.data.message || 'Role created successfully',
        data: response.data.role || null,
        status: response.status
      };
    } catch (error) {
      console.error('❌ Create role error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  },

  // Update role
  updateRole: async (uuid, name, description = '') => {
    if (!uuid) {
      return {
        success: false,
        message: 'Role ID is required',
        status: 400
      };
    }
    if (!name) {
      return {
        success: false,
        message: 'Role name is required',
        status: 400
      };
    }
    try {
      const response = await api.put(endpoints.retailone.rolesUpdate(uuid), {
        name,
        description
      });
      return {
        success: response.data.success || false,
        message: response.data.message || 'Role updated successfully',
        data: response.data.role || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Update role ${uuid} error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  },

  // Delete role
  deleteRole: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'Role ID is required',
        status: 400
      };
    }
    try {
      const response = await api.delete(endpoints.retailone.rolesDelete(uuid));
      return {
        success: response.data.success || false,
        message: response.data.message || 'Role deleted successfully',
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Delete role ${uuid} error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500
      };
    }
  },

  // Get role permissions
  getRolePermissions: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'Role ID is required',
        status: 400
      };
    }
    try {
      const response = await api.get(endpoints.retailone.rolesPermissions(uuid));
      return {
        success: true,
        data: response.data || { role: null, permissions: [] },
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Get role permissions ${uuid} error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: { role: null, permissions: [] }
      };
    }
  },

  // Assign permission to role
  assignPermission: async (roleUuid, permissionUuid) => {
    if (!roleUuid || !permissionUuid) {
      return {
        success: false,
        message: 'Role ID and Permission ID are required',
        status: 400
      };
    }
    try {
      const response = await api.post(
        endpoints.retailone.rolesAssignPermission(roleUuid),
        { permission_uuid: permissionUuid }
      );
      return {
        success: response.data.success || false,
        message: response.data.message || 'Permission assigned successfully',
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Assign permission error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500
      };
    }
  },

  // Remove permission from role
  removePermission: async (roleUuid, permissionUuid) => {
    if (!roleUuid || !permissionUuid) {
      return {
        success: false,
        message: 'Role ID and Permission ID are required',
        status: 400
      };
    }
    try {
      const response = await api.delete(
        endpoints.retailone.rolesRemovePermission(roleUuid, permissionUuid)
      );
      return {
        success: response.data.success || false,
        message: response.data.message || 'Permission removed successfully',
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Remove permission error:`, error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500
      };
    }
  }
};

export default roleManagementService;